import { isArray } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { QueryParamNames } from '~/constants/search';

type PendingRemap = {
  from: string;
  to: string;
  toIsOptimistic: boolean;
};

// Suppress stale-id guard while change-person remap updates cache before URL.
let pendingRemap: PendingRemap | null = null;

type MarkPendingClipPersonIdRemapOptions = {
  toIsOptimistic?: boolean;
};

export const markPendingClipPersonIdRemap = (
  from: string,
  to: string,
  { toIsOptimistic = false }: MarkPendingClipPersonIdRemapOptions = {},
) => {
  pendingRemap = { from, to, toIsOptimistic };
};

export const clearPendingClipPersonIdRemap = () => {
  pendingRemap = null;
};

export const isPendingClipPersonIdRemap = (clipPersonId: string) => pendingRemap?.from === clipPersonId;

/** Map a URL `clipPersonId` to the in-cache id while a change-person remap is in flight. */
export const resolveClipPersonSelectionId = (selectedClipPersonId: string) => {
  const pending = pendingRemap;

  if (pending?.from === selectedClipPersonId) {
    return pending.to;
  }

  return selectedClipPersonId;
};

/** Resolve a clip-person id for API calls; returns null while remap target is still optimistic. */
export const resolveClipPersonIdForApi = (clipPersonId: string): string | null => {
  const pending = pendingRemap;

  if (pending?.from === clipPersonId) {
    return pending.toIsOptimistic ? null : pending.to;
  }

  return clipPersonId;
};

/** True when add-bbox mode targets this clip-person row, including during id remap. */
export const pendingBoundingBoxTargetsClipPerson = (
  pendingClipPersonBoundingBoxId: string | null,
  clipPersonId: string,
): boolean => {
  if (!pendingClipPersonBoundingBoxId) {
    return false;
  }

  if (pendingClipPersonBoundingBoxId === clipPersonId) {
    return true;
  }

  return resolveClipPersonSelectionId(pendingClipPersonBoundingBoxId) === clipPersonId;
};

export const pendingBoundingBoxTargetsDifferentClipPerson = (
  pendingClipPersonBoundingBoxId: string | null,
  clipPersonId: string,
): boolean =>
  !!pendingClipPersonBoundingBoxId &&
  !pendingBoundingBoxTargetsClipPerson(pendingClipPersonBoundingBoxId, clipPersonId);

/** Clears pending remap once the URL catches up to the new clip-person id. */
export const useSyncClipPersonIdRemap = () => {
  const { query } = useRouter();
  const clipPersonIdValue = query[QueryParamNames.clipPersonId];

  const clipPersonId = isArray(clipPersonIdValue) ? clipPersonIdValue[0] : clipPersonIdValue;

  useEffect(() => {
    if (pendingRemap?.to === clipPersonId) {
      clearPendingClipPersonIdRemap();
    }
  }, [clipPersonId]);
};
